import ReactGA from "react-ga4";
import { getConsent } from "../providers/ConsentProvider";
import { debugLog } from "./logUtils";

/**
 * Track a pageview (respects analytics consent)
 */
export function trackPageview(title: string) {
  const consent = getConsent();
  if (consent?.analytics) {
    ReactGA.send({ hitType: "pageview", title });
  }
}

/**
 * Track an analytics event (respects user consent preferences)
 *
 * - Analytics consent required for: Google Analytics
 * - Marketing consent required for: Meta Pixel
 *
 * @param event - Event name
 * @param value - (Optional) Event value for revenue tracking. Defaults to 0 for non-revenue events.
 * @param currency - (Optional) Currency code (e.g., 'USD'). Defaults to 'USD'.
 */
export function trackEvent(event: string, value: number = 0, currency: string = "USD") {
  const consent = getConsent();

  debugLog(`[DEBUG ${new Date().toISOString()}] trackEvent: ${event} (analytics=${consent?.analytics ?? false}, marketing=${consent?.marketing ?? false})`);

  // Only track analytics if user has given consent
  if (consent?.analytics) {
    ReactGA.event(event);
    debugLog(`[DEBUG ${new Date().toISOString()}] Sent to Google Analytics: ${event}`);
  }

  // Only track marketing pixels if user has given consent
  // Fire pixels in production OR in dev mode if VITE_FORCE_PIXELS is set
  const shouldFirePixel = (!import.meta.env.DEV || import.meta.env.VITE_FORCE_PIXELS) && consent?.marketing;

  if (shouldFirePixel) {
    if (typeof fbq === "function") {
      fbq("trackCustom", event, { value, currency });
      debugLog(`[DEBUG ${new Date().toISOString()}] Sent to Meta Pixel: ${event} {value: ${value}, currency: ${currency}}`);
    } else {
      debugLog(`[DEBUG ${new Date().toISOString()}] Meta Pixel not available (fbq is not a function)`);
    }
  } else if (import.meta.env.DEV && !import.meta.env.VITE_FORCE_PIXELS && consent?.marketing) {
    // Dev mode without VITE_FORCE_PIXELS: just log what would be sent
    debugLog(`[DEBUG ${new Date().toISOString()}] Would send to Meta Pixel (DEV mode, VITE_FORCE_PIXELS not set): ${event} {value: ${value}, currency: ${currency}}`);
  }
}
